import React from 'react';
import { RefreshCw } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export interface SensorChartStyle01Props {
  data: Array<{ time: string; value: number; [key: string]: any }>;
  dataKey?: string;
  loading?: boolean;
  title?: string;
  color?: string;
  unit?: string;
  height?: number;
}

/**
 * SensorChart Style 01 - Line Chart
 * Clean line chart for time series data
 */
export const Style01_Line: React.FC<SensorChartStyle01Props> = ({
  data,
  dataKey = 'value',
  loading = false,
  title = '歷史趨勢',
  color = '#06b6d4',
  unit = '',
  height = 200
}) => {
  // 智能格式化 Y 軸刻度，根據數據範圍選擇適當精度
  const formatYAxisTick = (value: number): string => {
    if (value === 0) return '0';
    const absValue = Math.abs(value);
    if (absValue >= 1000) return `${(value / 1000).toFixed(0)}k`;
    if (absValue >= 100) return value.toFixed(0);
    if (absValue >= 10) return value.toFixed(1);
    if (absValue >= 1) return value.toFixed(1);
    if (absValue >= 0.1) return value.toFixed(2);
    return value.toFixed(3);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-slate-300">{title}</h4>
        {loading && (
          <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
        )}
      </div>
      <div className="bg-slate-800/30 rounded-xl p-4">
        {loading ? (
          <div className="flex items-center justify-center" style={{ height }}>
            <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          </div>
        ) : data.length > 0 ? (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis 
                dataKey="time" 
                stroke="#64748b" 
                tick={{ fill: '#94a3b8', fontSize: 10 }}
              />
              {/* Y 軸從 0 開始，使用智能格式化避免小數值顯示為 0 */}
              <YAxis 
                stroke="#64748b" 
                tick={{ fill: '#94a3b8', fontSize: 10 }}
                unit={unit}
                width={50}
                domain={[0, 'auto']}
                tickFormatter={formatYAxisTick}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1e293b', 
                  border: '1px solid #334155',
                  borderRadius: '8px'
                }}
                formatter={(value) => {
                  // 智能格式化 tooltip 數值
                  if (typeof value !== 'number') {
                    return ['--', title];
                  }
                  const formatted = value >= 1000 
                    ? `${(value/1000).toFixed(2)}k` 
                    : value >= 1 
                      ? value.toFixed(2)
                      : value.toFixed(3);
                  return [`${formatted}${unit}`, title];
                }}
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: color }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center text-slate-500 text-sm" style={{ height }}>
            暫無歷史數據
          </div>
        )}
      </div>
    </div>
  );
};

export default Style01_Line;
